import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '../ui/Button';
import { useCabinetTags, useSetCabinetFileTags } from '../../hooks/queries/useCabinetLibrary';
import { listFileTagIds } from '../../services/cabinet/cabinetService';
import type { CabinetFile } from '../../services/cabinet/cabinetService';

interface Props {
  file: CabinetFile;
  onClose: () => void;
}

export function CabinetFileTagsModal({ file, onClose }: Props) {
  const { data: tags = [] } = useCabinetTags();
  const setTags = useSetCabinetFileTags();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    listFileTagIds(file.id).then((ids) => {
      setSelected(new Set(ids));
      setLoaded(true);
    });
  }, [file.id]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSave() {
    await setTags.mutateAsync({ fileId: file.id, tagIds: Array.from(selected) });
    onClose();
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-md shadow-xl w-full max-w-sm max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between px-5 py-3 border-b border-neutral-200">
          <h2 className="font-heading text-lg font-semibold truncate">Tags · {file.filename}</h2>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-700">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {!loaded ? (
            <p className="text-sm text-neutral-500">Chargement…</p>
          ) : tags.length === 0 ? (
            <p className="text-sm text-neutral-500 italic">
              Aucun tag défini. Demande à un admin de créer des tags depuis la bibliothèque.
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {tags.map((t) => {
                const on = selected.has(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => toggle(t.id)}
                    className={`text-xs px-2 py-1 rounded-full border transition ${
                      on ? 'text-white' : 'text-neutral-600 bg-white hover:bg-neutral-50'
                    }`}
                    style={
                      on
                        ? { backgroundColor: t.color, borderColor: t.color }
                        : { borderColor: t.color }
                    }
                  >
                    {t.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-neutral-200 bg-neutral-50">
          <Button variant="ghost" onClick={onClose} disabled={setTags.isPending}>Annuler</Button>
          <Button onClick={handleSave} disabled={!loaded || setTags.isPending}>
            {setTags.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </footer>
      </div>
    </div>
  );
}
